import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";

import {
  devLoginSkipVerification as devLoginSkipVerificationApi,
  devSkipVerification as devSkipVerificationApi,
  fetchMe,
  forgotPassword as forgotPasswordApi,
  login as loginApi,
  resetPassword as resetPasswordApi,
  signup as signupApi,
} from "@/features/auth/api/authApi";
import { useAuthStore } from "@/features/auth/model/authStore";
import type {
  ForgotPasswordPayload,
  LoginPayload,
  ResetPasswordPayload,
  SignupPayload,
  User,
} from "@/features/auth/types";
import { useShiftFeedStore } from "@/features/shifts/model/shiftFeedStore";

// Wipe per-user caches before installing a new session. Without this, a query
// like ["worker", "me"] keeps the previous account's profile until staleTime
// expires — which makes role-/status-/onboarding-based redirects misfire on
// the very first render after login (e.g. WorkerHomePage skipping the
// onboarding redirect because the prior user had it completed).
function clearPerUserState(queryClient: ReturnType<typeof useQueryClient>, resetShiftFeed: () => void) {
  queryClient.clear();
  resetShiftFeed();
}

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  const resetShiftFeed = useShiftFeedStore((s) => s.reset);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: LoginPayload) => loginApi(payload),
    onSuccess: (data) => {
      clearPerUserState(queryClient, resetShiftFeed);
      setSession(data.access_token, data.user);
      queryClient.setQueryData(["me"], data.user);
      navigate(data.user.role === "VENUE" ? "/venue" : "/worker");
    },
  });
}

export function useSignup() {
  return useMutation({
    mutationFn: (payload: SignupPayload) => signupApi(payload),
    // Intentionally do NOT set session on signup — users must verify their
    // email before they can log in. SignupForm shows a success banner.
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (payload: ForgotPasswordPayload) => forgotPasswordApi(payload),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (payload: ResetPasswordPayload) => resetPasswordApi(payload),
  });
}

export function useDevSkipVerification() {
  const setSession = useAuthStore((s) => s.setSession);
  const resetShiftFeed = useShiftFeedStore((s) => s.reset);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accessToken: string) => devSkipVerificationApi(accessToken),
    onSuccess: (user, accessToken) => {
      clearPerUserState(queryClient, resetShiftFeed);
      setSession(accessToken, user);
      queryClient.setQueryData(["me"], user);
      navigate(user.role === "VENUE" ? "/venue" : "/worker");
    },
  });
}

export function useDevLoginSkipVerification() {
  const setSession = useAuthStore((s) => s.setSession);
  const resetShiftFeed = useShiftFeedStore((s) => s.reset);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: LoginPayload) => devLoginSkipVerificationApi(payload),
    onSuccess: (data) => {
      clearPerUserState(queryClient, resetShiftFeed);
      setSession(data.access_token, data.user);
      queryClient.setQueryData(["me"], data.user);
      navigate(data.user.role === "VENUE" ? "/venue" : "/worker");
    },
  });
}

export function useLogout() {
  const clearSession = useAuthStore((s) => s.clearSession);
  const resetShiftFeed = useShiftFeedStore((s) => s.reset);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return () => {
    clearSession();
    clearPerUserState(queryClient, resetShiftFeed);
    navigate("/login");
  };
}

export function useCurrentUser() {
  const token = useAuthStore((s) => s.token);
  const storeUser = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  return useQuery<User>({
    queryKey: ["me"],
    queryFn: async () => {
      const user = await fetchMe();
      setUser(user);
      return user;
    },
    enabled: !!token,
    initialData: storeUser ?? undefined,
    retry: false,
  });
}

export function formatAuthError(error: unknown): string {
  if (error instanceof AxiosError) {
    if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
      return "Connection timed out. Check your internet and try again.";
    }
    if (error.code === "ERR_NETWORK") {
      return "Couldn't reach the server. Check your internet and try again.";
    }
    const detail = error.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  }
  return "Something went wrong. Please try again.";
}

export function isEmailVerificationError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    const detail = error.response?.data?.detail;
    if (typeof detail === "string") {
      return detail.toLowerCase().includes("verify your email");
    }
  }
  return false;
}
